import React, { useEffect, useRef, useState } from 'react';
import {
  createInventoryBin,
  createInventoryItem,
  deleteInventoryBin,
  fetchInventoryHistory,
  receiveInventoryItem,
  updateInventoryBin,
  updateInventoryItem,
  weighInventoryItem,
} from '../api';
import { bagLabel, errorMessage, formatDate, formatDateTime, formatQuantity, toNumber } from './inventoryFormat';

const Modal = ({ title, onClose, children, wide }) => {
  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="inv-modal-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className={`inv-modal ${wide ? 'inv-modal-wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="inv-modal-head">
          <h3>{title}</h3>
          <button type="button" className="inv-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        {children}
      </div>
    </div>
  );
};

const ItemSummary = ({ item }) => (
  <div className="inv-modal-summary">
    <strong>{item.name}</strong>
    <span className="inv-muted">
      {[item.bin && `Bin ${item.bin}`, item.category].filter(Boolean).join(' · ')}
    </span>
    <span className="inv-muted">
      Now: {item.quantity === null ? 'not weighed yet' : `${formatQuantity(item.quantity)} ${item.unit}`}
      {item.bags.length > 1 && ` in ${item.bags.length} bags`}
      {item.last_counted_at && ` · weighed ${formatDate(item.last_counted_at)}`}
    </span>
  </div>
);

// With more than one bag on the shelf, the person picks the bag they put on the scale,
// so the other bags keep their weight.
const BagPicker = ({ item, bagId, onPick }) => (
  <fieldset className="inv-bag-picker">
    <legend>This powder is in {item.bags.length} bags. Which one did you weigh?</legend>
    {item.bags.map((bag) => (
      <label key={bag.id} className={`inv-bag-option ${bagId === bag.id ? 'selected' : ''}`}>
        <input type="radio" name="inv-bag" checked={bagId === bag.id} onChange={() => onPick(bag.id)} />
        <span className="inv-qty">{formatQuantity(bag.quantity)} {item.unit}</span>
        <span className="inv-muted">{bagLabel(bag)}</span>
      </label>
    ))}
  </fieldset>
);

export const WeighDialog = ({ item, onClose, onSaved }) => {
  const [quantity, setQuantity] = useState('');
  const [bagId, setBagId] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const input = useRef(null);

  useEffect(() => { input.current && input.current.focus(); }, []);

  const severalBags = item.bags.length > 1;
  const reading = toNumber(quantity);
  const others = item.bags.filter((bag) => bag.id !== bagId).reduce((sum, bag) => sum + toNumber(bag.quantity), 0);

  const save = async (event) => {
    event.preventDefault();
    if (reading === null) {
      setError('Enter the weight from the scale.');
      return;
    }
    if (severalBags && bagId === null) {
      setError('Pick the bag you weighed.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      onSaved(await weighInventoryItem(item.id, { quantity, note, ...(severalBags ? { bag: bagId } : {}) }));
    } catch (err) {
      setError(errorMessage(err, 'Could not save the weight.'));
      setSaving(false);
    }
  };

  return (
    <Modal title="Weigh" onClose={onClose}>
      <ItemSummary item={item} />
      <form onSubmit={save} className="inv-form">
        <label className="inv-field">
          <span>Weight on the scale ({item.unit})</span>
          <input
            ref={input}
            className="inv-input inv-input-big"
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
          />
        </label>
        {severalBags && <BagPicker item={item} bagId={bagId} onPick={(id) => { setBagId(id); setError(''); }} />}
        {severalBags && bagId !== null && reading !== null && (
          <div className="inv-muted">New total: {formatQuantity(others + reading)} {item.unit}{reading === 0 && ' (this bag is used up)'}</div>
        )}
        <div className="inv-muted">{severalBags ? 'Weigh one bag at a time. ' : ''}Enter 0 if the bag is used up.</div>
        <label className="inv-field">
          <span>Note (optional)</span>
          <input className="inv-input" value={note} maxLength={500} onChange={(event) => setNote(event.target.value)} />
        </label>
        {error && <div className="inv-error">{error}</div>}
        <div className="inv-modal-actions">
          <button type="button" className="inv-button" onClick={onClose}>Cancel</button>
          <button type="submit" className="inv-button inv-button-primary" disabled={saving}>{saving ? 'Saving…' : 'Save weight'}</button>
        </div>
      </form>
    </Modal>
  );
};

export const ReceiveDialog = ({ item, onClose, onSaved }) => {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const input = useRef(null);

  useEffect(() => { input.current && input.current.focus(); }, []);

  const added = toNumber(amount);
  const total = added === null ? null : (toNumber(item.quantity) || 0) + added;

  const save = async (event) => {
    event.preventDefault();
    if (added === null || added <= 0) {
      setError('Enter how much came in.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      onSaved(await receiveInventoryItem(item.id, { amount, note }));
    } catch (err) {
      setError(errorMessage(err, 'Could not save.'));
      setSaving(false);
    }
  };

  return (
    <Modal title="Received" onClose={onClose}>
      <ItemSummary item={item} />
      <form onSubmit={save} className="inv-form">
        <label className="inv-field">
          <span>Amount received ({item.unit})</span>
          <input
            ref={input}
            className="inv-input inv-input-big"
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        </label>
        {total !== null && <div className="inv-muted">New total: {formatQuantity(total)} {item.unit}</div>}
        {toNumber(item.quantity) > 0 && (
          <div className="inv-muted">
            This goes on the shelf as a new bag, separate from the one already there. If you poured it into that bag instead, use Weigh.
          </div>
        )}
        <label className="inv-field">
          <span>Note (optional)</span>
          <input className="inv-input" value={note} maxLength={500} placeholder="Order or invoice number" onChange={(event) => setNote(event.target.value)} />
        </label>
        {error && <div className="inv-error">{error}</div>}
        <div className="inv-modal-actions">
          <button type="button" className="inv-button" onClick={onClose}>Cancel</button>
          <button type="submit" className="inv-button inv-button-primary" disabled={saving}>{saving ? 'Saving…' : 'Add to stock'}</button>
        </div>
      </form>
    </Modal>
  );
};

// A powder's bin is picked from the bins that exist, so a typo can't start a new one.
const BinSelect = ({ bins, value, onChange, big, inputRef }) => (
  <select ref={inputRef} className={`inv-input ${big ? 'inv-input-big' : ''}`} value={value} onChange={(event) => onChange(event.target.value)}>
    <option value="">No bin</option>
    {bins.map((b) => <option key={b.id} value={b.name}>Bin {b.name}{b.description && ` · ${b.description}`}</option>)}
  </select>
);

// Quick move from the Bins page: only the bin changes.
export const BinDialog = ({ item, bins, onClose, onSaved }) => {
  const [bin, setBin] = useState(item.bin || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const input = useRef(null);

  useEffect(() => { input.current && input.current.focus(); }, []);

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      onSaved(await updateInventoryItem(item.id, { bin }));
    } catch (err) {
      setError(errorMessage(err, 'Could not save the bin.'));
      setSaving(false);
    }
  };

  return (
    <Modal title="Change bin" onClose={onClose}>
      <ItemSummary item={item} />
      <form onSubmit={save} className="inv-form">
        <label className="inv-field">
          <span>Bin</span>
          <BinSelect bins={bins} value={bin} onChange={setBin} big inputRef={input} />
        </label>
        {error && <div className="inv-error">{error}</div>}
        <div className="inv-modal-actions">
          <button type="button" className="inv-button" onClick={onClose}>Cancel</button>
          <button type="submit" className="inv-button inv-button-primary" disabled={saving}>{saving ? 'Saving…' : 'Save bin'}</button>
        </div>
      </form>
    </Modal>
  );
};

// Add or edit a bin from the Bins page. Renaming moves its powders with it; only an empty
// bin can be deleted.
export const BinEditDialog = ({ bin, onClose, onSaved }) => {
  const [name, setName] = useState(bin ? bin.name : '');
  const [description, setDescription] = useState(bin ? bin.description : '');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');

  const save = async (event) => {
    event.preventDefault();
    if (!name.trim()) {
      setError('Enter the bin name or number.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const data = { name, description };
      await (bin ? updateInventoryBin(bin.id, data) : createInventoryBin({ ...data, kind: 'powder' }));
      onSaved();
    } catch (err) {
      setError(errorMessage(err, 'Could not save the bin.'));
      setSaving(false);
    }
  };

  const remove = async () => {
    // A second click confirms, so a stray tap doesn't delete it.
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await deleteInventoryBin(bin.id);
      onSaved();
    } catch (err) {
      setError(errorMessage(err, 'Could not delete the bin.'));
      setSaving(false);
    }
  };

  return (
    <Modal title={bin ? `Edit bin ${bin.name}` : 'Add bin'} onClose={onClose}>
      <form onSubmit={save} className="inv-form">
        <div className="inv-field-row">
          <label className="inv-field inv-field-small">
            <span>Bin</span>
            <input className="inv-input" value={name} maxLength={20} placeholder="33" onChange={(event) => setName(event.target.value)} autoFocus />
          </label>
          <label className="inv-field">
            <span>What goes here (optional)</span>
            <input className="inv-input" value={description} maxLength={100} placeholder="Black" onChange={(event) => setDescription(event.target.value)} />
          </label>
        </div>
        {bin && bin.item_count > 0 && (
          <div className="inv-muted">
            {bin.item_count} powder{bin.item_count === 1 ? '' : 's'} in this bin. Renaming it keeps them here.
          </div>
        )}
        {error && <div className="inv-error">{error}</div>}
        <div className="inv-modal-actions">
          {bin && bin.item_count === 0 && (
            <button type="button" className="inv-button inv-button-danger inv-button-push-left" onClick={remove} disabled={saving}>
              {confirmDelete ? 'Click again to delete' : 'Delete bin'}
            </button>
          )}
          <button type="button" className="inv-button" onClick={onClose}>Cancel</button>
          <button type="submit" className="inv-button inv-button-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  );
};

const EMPTY_FORM = {
  name: '', category: '', bin: '', low_stock_level: '3', supplier: '', part_number: '', product_url: '', notes: '', is_active: true,
};

export const PowderFormDialog = ({ item, bins, categories, suppliers, onClose, onSaved }) => {
  const [form, setForm] = useState(() => (item
    ? { ...EMPTY_FORM, ...Object.fromEntries(Object.keys(EMPTY_FORM).map((key) => [key, item[key] ?? EMPTY_FORM[key]])), low_stock_level: formatQuantity(item.low_stock_level) }
    : EMPTY_FORM));
  const [startingQuantity, setStartingQuantity] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (key) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [key]: value }));
  };

  const save = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setError('Enter the color name.');
      return;
    }
    const data = { ...form, low_stock_level: form.low_stock_level === '' ? '3' : form.low_stock_level };
    if (!item) {
      data.kind = 'powder';
      if (startingQuantity !== '') data.starting_quantity = startingQuantity;
    }
    setSaving(true);
    setError('');
    try {
      onSaved(item ? await updateInventoryItem(item.id, data) : await createInventoryItem(data));
    } catch (err) {
      setError(errorMessage(err, 'Could not save.'));
      setSaving(false);
    }
  };

  return (
    <Modal title={item ? 'Edit powder' : 'Add powder'} onClose={onClose}>
      <form onSubmit={save} className="inv-form">
        <label className="inv-field">
          <span>Color name</span>
          <input className="inv-input" value={form.name} maxLength={200} onChange={set('name')} autoFocus={!item} />
        </label>
        <div className="inv-field-row">
          <label className="inv-field">
            <span>Category</span>
            <input className="inv-input" list="inv-categories" value={form.category} maxLength={50} onChange={set('category')} />
          </label>
          <label className="inv-field inv-field-small">
            <span>Bin</span>
            <BinSelect bins={bins} value={form.bin} onChange={(value) => setForm((current) => ({ ...current, bin: value }))} />
          </label>
        </div>
        <div className="inv-field-row">
          <label className="inv-field">
            <span>Low stock at ({item ? item.unit : 'lbs'})</span>
            <input className="inv-input" type="number" inputMode="decimal" step="0.25" min="0" value={form.low_stock_level} onChange={set('low_stock_level')} />
          </label>
          {!item && (
            <label className="inv-field">
              <span>Weight now (optional)</span>
              <input className="inv-input" type="number" inputMode="decimal" step="0.01" min="0" value={startingQuantity} onChange={(event) => setStartingQuantity(event.target.value)} />
            </label>
          )}
        </div>
        <div className="inv-field-row">
          <label className="inv-field">
            <span>Supplier</span>
            <input className="inv-input" list="inv-suppliers" value={form.supplier} maxLength={100} onChange={set('supplier')} />
          </label>
          <label className="inv-field">
            <span>Part number</span>
            <input className="inv-input" value={form.part_number} maxLength={50} onChange={set('part_number')} />
          </label>
        </div>
        <label className="inv-field">
          <span>Product link</span>
          <input className="inv-input" type="url" value={form.product_url} maxLength={500} placeholder="https://www.prismaticpowders.com/…" onChange={set('product_url')} />
        </label>
        <label className="inv-field">
          <span>Notes</span>
          <textarea className="inv-input" rows={2} value={form.notes} onChange={set('notes')} />
        </label>
        {item && (
          <label className="inv-check">
            <input type="checkbox" checked={!form.is_active} onChange={(event) => setForm((current) => ({ ...current, is_active: !event.target.checked }))} />
            No longer stocked (hide from the list)
          </label>
        )}
        <datalist id="inv-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
        <datalist id="inv-suppliers">{suppliers.map((s) => <option key={s} value={s} />)}</datalist>
        {error && <div className="inv-error">{error}</div>}
        <div className="inv-modal-actions">
          <button type="button" className="inv-button" onClick={onClose}>Cancel</button>
          <button type="submit" className="inv-button inv-button-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  );
};

const changeText = (change, unit) => {
  if (change.reason === 'received') {
    const newBag = change.bag !== null && toNumber(change.quantity_before) > 0 ? ' (new bag)' : '';
    return `+${formatQuantity(change.amount)} ${unit}${newBag} → ${formatQuantity(change.quantity_after)} ${unit}`;
  }
  if (change.quantity_after === null) return 'No weight';
  // One bag of several: show that bag's change, then the new total.
  const otherBags = (toNumber(change.quantity_after) || 0) - (toNumber(change.bag_after) || 0);
  if (change.reason === 'weighed' && change.bag_before !== null && otherBags > 0) {
    return `One bag ${formatQuantity(change.bag_before)} → ${formatQuantity(change.bag_after)} ${unit} · total ${formatQuantity(change.quantity_after)} ${unit}`;
  }
  const before = change.quantity_before === null ? '' : `${formatQuantity(change.quantity_before)} → `;
  return `${before}${formatQuantity(change.quantity_after)} ${unit}`;
};

export const HistoryDialog = ({ item, onClose, onAction }) => {
  const [changes, setChanges] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    fetchInventoryHistory(item.id)
      .then((data) => { if (active) setChanges(data); })
      .catch(() => { if (active) setError('Could not load the history.'); });
    return () => { active = false; };
  }, [item.id, item.quantity]);

  return (
    <Modal title="Powder details" onClose={onClose} wide>
      <ItemSummary item={item} />
      <div className="inv-detail-facts">
        <span>Low stock at {formatQuantity(item.low_stock_level)} {item.unit}</span>
        {item.supplier && <span>{item.supplier}{item.part_number && ` · ${item.part_number}`}</span>}
        {item.product_url && <a href={item.product_url} target="_blank" rel="noopener noreferrer">Product page ↗</a>}
        {!item.is_active && <span className="inv-tag">No longer stocked</span>}
      </div>
      {item.bags.length > 1 && (
        <div className="inv-detail-facts inv-bags-list">
          <strong>{item.bags.length} bags:</strong>
          {item.bags.map((bag) => (
            <span key={bag.id}>{formatQuantity(bag.quantity)} {item.unit} <span className="inv-muted">({bagLabel(bag)})</span></span>
          ))}
        </div>
      )}
      {item.notes && <div className="inv-detail-notes">{item.notes}</div>}
      <div className="inv-modal-actions inv-modal-actions-left">
        <button type="button" className="inv-button inv-button-primary" onClick={() => onAction('weigh')}>Weigh</button>
        <button type="button" className="inv-button" onClick={() => onAction('receive')}>Received</button>
        <button type="button" className="inv-button" onClick={() => onAction('edit')}>Edit</button>
      </div>
      <div className="inv-section-title">History</div>
      {error && <div className="inv-error">{error}</div>}
      {!changes && !error && <div className="inv-muted">Loading…</div>}
      {changes && changes.length === 0 && <div className="inv-muted">Nothing yet.</div>}
      {changes && changes.length > 0 && (
        <div className="inv-history">
          {changes.map((change) => (
            <div key={change.id} className="inv-history-row">
              <span className={`inv-history-reason inv-reason-${change.reason}`}>{change.reason_display}</span>
              <span className="inv-history-amount">{changeText(change, item.unit)}</span>
              <span className="inv-muted">
                {formatDateTime(change.created_at)}
                {change.created_by && ` · ${change.created_by.name}`}
              </span>
              {change.note && <span className="inv-history-note">{change.note}</span>}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};
